import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { cloneDeep } from 'lodash';

import { driverService } from '../../../../../application/DriverService';
import { Driver } from '../../../../../domain/driver/Driver';
import { Stop } from '../../../../../domain/stop/Stop';
import useErrorMiddleware from '../../../../../hooks/errorMiddleware/useErrorMiddleware';
import { useRetrieveActiveProject } from '../../../../../hooks/project/useRetrieveActiveProject';
import { useShowSnackbarMessage } from '../../../../../hooks/snackbarMessage/useShowSnackbarMessage';
import { PlanDetailsActionType } from '../../context/actions';
import { usePlanDetailsContext } from '../../context/context';
import { useRetrieveActivePlan } from '../plan-details/useRetrieveActivePlan';
import { useRetrieveActivePlanDrivers } from '../plan-details/useRetrieveActivePlanDrivers';
import { useRetrieveActivePlanStops } from '../plan-details/useRetrieveActivePlanStops';

type DriverStopsUpdateProps = { stops: Stop[]; index: number; driver?: Driver };

type UseUpdateDriverStopsResponse = ServiceResponse<({ driver, stops }: DriverStopsUpdateProps) => void, void>;

// Hooks act as 'Adapter' layer.
export const useUpdateDriverStops = (): UseUpdateDriverStopsResponse => {
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: activePlan } = useRetrieveActivePlan();
  const { map: driversMap } = useRetrieveActivePlanDrivers();
  const { map: stopsMap } = useRetrieveActivePlanStops();
  const { planDetailsDispatch } = usePlanDetailsContext();
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const queryClient = useQueryClient();

  const { isLoading, isSuccess, isError, mutate } = useMutation(
    async (props: DriverStopsUpdateProps): Promise<void> => {
      await driverService.updateDriverStops({ ...props, project: activeProject! });
    },
    {
      onMutate: ({ index, stops, driver }: DriverStopsUpdateProps) => {
        // Optimistically update to the new value
        const newDriversMap = cloneDeep(driversMap);
        const newStopsMap = cloneDeep(stopsMap);

        const updateStops: Stop[] = [];

        for (const stop of stops) {
          if (
            stop.type === `delivery` &&
            stop.pickup &&
            !stops.find((s) => s.id === stop.pickup!.id) &&
            stop.driver_id !== stop.pickup.driver_id
          ) {
            const pickup = stopsMap[stop.pickup.id];
            if (!pickup) {
              throw new Error(`Pickup not found for delivery stop`);
            }
            updateStops.push(pickup);
          }

          updateStops.push(stopsMap[stop.id]);

          if (stop.type === `pickup` && !stops.find((s) => s.pickup_id === stop.id)) {
            const delivery = Object.values(stopsMap).find((s) => s.pickup_id === stop.id);
            if (stop.driver_id !== delivery?.driver_id) {
              if (!delivery) {
                throw new Error(`Delivery not found for pickup stop`);
              }
              updateStops.push(delivery);
            }
          }
        }

        // Reschedule affected routes
        updateStops.forEach((stop) => {
          if (stop.driver_id && newDriversMap[stop.driver_id]) {
            newDriversMap[stop.driver_id].stops = newDriversMap[stop.driver_id].stops.filter((s) => s.id !== stop.id);
            newDriversMap[stop.driver_id].stops = newDriversMap[stop.driver_id].stops.map((s, idx) => {
              s.order = idx;
              s.planned_arrival_time = undefined;
              s.planned_departure_time = undefined;
              s.distance_to_previous_stop = undefined;
              s.distance_to_next_stop = undefined;

              newStopsMap[s.id] = s;
              return s;
            });
          }
        });

        // unassign stops from driver
        if (!driver) {
          updateStops.forEach((stop) => {
            stop.driver_id = undefined;
            newStopsMap[stop.id] = stop;
          });
        } else {
          // remove stops from driver.stops
          const newDriver = newDriversMap[driver.id];
          if (newDriver) {
            const stopsToAssign = newDriver.stops.filter(
              (driverStop) => !updateStops.some((stop) => stop.id === driverStop.id)
            );
            const chunk1 = stopsToAssign.slice(0, index);
            const chunk2 = stopsToAssign.slice(index);
            newDriver.stops = ([] as Stop[]).concat(
              chunk1,
              updateStops.map((s) => newStopsMap[s.id]),
              chunk2
            );
            newDriver.stops = newDriver.stops.map((s, idx) => {
              s.driver_id = newDriver.id;
              s.order = idx;
              s.planned_arrival_time = undefined;
              s.planned_departure_time = undefined;
              s.distance_to_previous_stop = undefined;
              s.distance_to_next_stop = undefined;

              newStopsMap[s.id] = s;
              return s;
            });
          }
        }

        planDetailsDispatch({
          type: PlanDetailsActionType.SET_ACTIVE_PLAN_DRIVERS,
          payload: {
            planDrivers: Object.values(newDriversMap),
          },
        });

        planDetailsDispatch({
          type: PlanDetailsActionType.SET_ACTIVE_PLAN_STOPS,
          payload: {
            planStops: Object.values(newStopsMap),
          },
        });

        queryClient.cancelQueries([`planner_active_plan_drivers`, activePlan?.id]);
        queryClient.cancelQueries([`planner_active_plan_stops`, activePlan?.id]);

        queryClient.setQueryData([`planner_active_plan_drivers`, activePlan?.id], Object.values(newDriversMap));
        queryClient.setQueryData([`planner_active_plan_stops`, activePlan?.id], Object.values(newStopsMap));
      },
      onError: (err: any) => {
        console.info(`Update Driver Stops Error:`, err);
        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }

        // To make sure that we have the same information with the DB, we invalidate the cache
        queryClient.invalidateQueries([`planner_active_plan_drivers`, activePlan?.id]);
        queryClient.invalidateQueries([`planner_active_plan_stops`, activePlan?.id]);

        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  return {
    run: mutate,
    error: isError,
    success: isSuccess,
    isLoading: isLoading,
  };
};
